// Jack of All Threads - a crowdfunding platform for custom apparel, built in
// Bangalore. Faraz's role: Senior UX / UI Designer.
//
// DE-ANONYMISED 2026-09-26. The company no longer trades, so the real name,
// the real press list and the real charity partners are all back, and the
// screenshots were restored from 40541d7^ (the versions before
// scripts/rebrand-case-screens.py replaced the wordmark). This study also
// left the vault on the same date: nothing here is confidential, so the
// pages import this module directly instead of reading it through
// CaseStudyGate.
//
// The `deck` block still carries the constructed outcome figures from the
// August rewrite; see the METRICS note there. The v2 block that the new page
// renders deliberately does not use them.
// Image w/h are intrinsic pixel sizes (reserve space, prevent layout shift).
//
// REWRITTEN 2026-08-04: cut to a ~3 minute read, same treatment as FinVista.
// All keys preserved; values shortened, screen counts trimmed. Full-length
// material is in git history.

const J = (f) => `/joat/${f}`;

export const joat = {
  slug: "joat",
  title: "Jack of All Threads",
  subtitle:
    "A crowdfunding platform for custom apparel, designed from nothing: design a tee, set a goal, and sell it before a single shirt is printed.",

  hero: {
    cover: "/joat/cover.jpg",
    facts: [
      { label: "Role", value: "Senior UX / UI Designer" },
      { label: "Domain", value: "Crowdfunding commerce" },
      { label: "Platform", value: "Responsive web + mobile" },
    ],
    stats: [
      { value: "1st", label: "Crowdfunding platform for custom apparel in India" },
      { value: "₹0", label: "Upfront cost to the creator" },
      { value: "6+", label: "Tools and vendors collapsed into one flow" },
    ],
  },

  // --- 01 OVERVIEW ---
  overview: {
    headline: "Project Overview",
    tldrTitle: "The short version",
    tldr:
      "Jack of All Threads was India's first crowdfunding platform for custom apparel. You design a tee in the browser, set a sales goal, and sell it before anything gets printed. I led the UX and visual redesign end to end, covering the Design Studio, the campaign flow, the buyer journey and the raiser dashboards. The Hindu, Deccan Herald, DNA and YourStory all covered it, and CRY and the Akshaya Patra Foundation ran campaigns on it.",
    facts: [
      { label: "Role", value: "Senior UX / UI Designer" },
      { label: "Company", value: "Jack of All Threads, Bangalore" },
      { label: "Platform", value: "Responsive Web + Mobile" },
      { label: "Scope", value: "End-to-end product redesign" },
    ],
    intro:
      "This is the oldest work on the site, from 2015, and it stays because the product was ahead of its time. Jack of All Threads started as a college project and became India's first t-shirt crowdfunding platform. I owned the interface end to end and rebuilt it as one platform rather than a set of separate tools.",
    intro2:
      "The redesign had one job: take a process that used to span six disconnected tools and vendors, and make it one calm flow anyone could finish in an afternoon.",
    contextTitle: "The model in one line",
    contextBody:
      "You publicize. We produce. You keep the profit. Nobody pays upfront and nothing is printed until the campaign is funded.",
  },

  primaryUsers: [
    { label: "Campaign Raisers", desc: "Students, clubs, bands, artists and small brands who run a campaign with no upfront cost and no inventory." },
    { label: "Cause & NGO Raisers", desc: "National children's charities raising funds and awareness through merchandise campaigns." },
    { label: "Buyers / Backers", desc: "Supporters who land from a shared link or a Facebook ad and order the tee, which is what funds the run." },
  ],

  // --- 02 PROBLEM ---
  problem: {
    headline: "Selling custom merch meant juggling a stack of tools",
    coreChallenge:
      "One tool to design, another to mock up, a printer with high minimum orders, a way to take payments, and your own shipping. Every handoff added cost, dependency and risk before a single shirt sold.",
    dimensions: [
      { title: "A stack of disconnected tools", desc: "Nothing talked to anything, and the creator carried every handoff." },
      { title: "Cost and risk upfront", desc: "High minimum orders and pay-first printing put real money on the line before any demand was proven." },
      { title: "Promote to strangers", desc: "Campaigns lived or died on a link dropped into Facebook ads, so the funnel had to convert cold traffic." },
    ],
    hypothesisTitle: "The redesign bet",
    hypothesis:
      "If one platform owns the entire journey, design, pricing, payments, production, shipping and payout, then a creator can launch with zero upfront cost and nothing to wrangle but the idea.",
    hypothesisBullets: ["One platform for all", "Zero upfront cost", "Nothing to wrangle"],
  },

  // --- 03 HOW IT WORKS ---
  howItWorks: {
    headline: "How a campaign works",
    intro:
      "The whole product is one loop, surfaced right on the homepage, so a first-time raiser gets it before they sign up.",
    steps: [
      { n: "01", t: "Design in minutes", d: "Pick a product and color, drop in artwork or text, and preview it live with an instant base cost." },
      { n: "02", t: "Set your price and promote", d: "Choose a goal, an end date and a per-tee price; the flow shows the minimum profit before launch. Then share the link." },
      { n: "03", t: "We produce, you profit", d: "If the goal is met we print the shirts, ship them and transfer the profit. If it is missed, nobody pays anything, so the creator is never holding stock." },
    ],
    image: { src: J("01-homepage.jpg"), w: 1400, h: 3920, caption: "Homepage: the model explained to a first-time raiser, with live campaigns and press credibility." },
  },

  // --- 04 INFORMATION ARCHITECTURE ---
  ia: {
    headline: "Structuring a stack of tools into one platform",
    intro:
      "A creator tool, a public storefront and a back office in one place, organized around three jobs: create a campaign, back a campaign, manage what you launched.",
    diagram: J("ia.svg"),
    diagramW: 1080,
    diagramH: 560,
    caption: "Information architecture: the creator studio, the buyer storefront, and the raiser back office under one roof.",
  },

  // --- 05 USER FLOW ---
  userFlow: {
    headline: "One journey, two sides",
    intro:
      "The raiser designs, prices and launches. The buyer arrives from an ad and orders. Everything in between, production, payments, shipping, is the platform's job, not theirs.",
    diagram: J("flow.svg"),
    diagramW: 1080,
    diagramH: 440,
    caption: "The raiser and buyer journeys, meeting at the live campaign page.",
  },

  // --- 06 WIREFRAMES ---
  wireframes: {
    headline: "From graybox layouts to the final product",
    intro:
      "The heaviest screens blocked out in grayscale first, a few layout options each, pressure-tested on their core job.",
    batches: [
      { src: J("wf-studio.svg"), w: 1080, h: 580, caption: "Design Studio: left rail vs top toolbar vs floating panels. The left rail won, keeping the live tee preview central." },
      { src: J("wf-campaign.svg"), w: 1080, h: 580, caption: "Set Price and Goal: economics buried in a form vs a live profit panel beside the tee. The live panel made the money obvious." },
      { src: J("wf-home.svg"), w: 1080, h: 580, caption: "Homepage: explain-then-browse won, since most visitors had never seen crowdfunded tees." },
    ],
  },

  // --- 07 DESIGN STUDIO ---
  studio: {
    headline: "The Design Studio: design in the browser, nothing to install",
    intro:
      "Instead of design software, an export and an email to a printer, a creator designs right here, with a live preview and a running base cost, inside the flow that ends in a live campaign.",
    image: { src: J("02-design-studio.jpg"), w: 1400, h: 870, caption: "Design Studio: central live preview, one controls rail, and base cost surfaced as you design." },
    points: [
      { t: "No installs, no handoffs", d: "The file never leaves the platform on its way to print." },
      { t: "Live tee preview", d: "Front and back, real product colors, and a curated artwork library." },
      { t: "Cost as you go", d: "A base cost sits beside the canvas, so the economics start before the campaign flow opens." },
    ],
  },

  // --- 08 CAMPAIGN FLOW ---
  campaign: {
    headline: "Making the money obvious",
    intro:
      "The pricing step is where most platforms hide the economics; I pulled them into the open. A goal slider, an end date and a per-tee price update a live minimum-profit figure, and the no-risk promise sits beside the CTA.",
    screens: [
      { src: J("03-set-price.jpg"), w: 1400, h: 870, title: "Set Your Price", desc: "Goal, end date and selling price update a live minimum-profit figure." },
      { src: J("04-add-description.jpg"), w: 1400, h: 870, title: "Add a Description", desc: "The story and details that make a campaign shareable." },
      { src: J("05-campaign.jpg"), w: 1400, h: 870, title: "Campaign Page", desc: "The public page a raiser shares to convert cold traffic into buyers." },
    ],
  },

  // --- 09 RAISER DASHBOARD ---
  dashboard: {
    headline: "Running the campaign after launch",
    intro:
      "Orders, goal progress and payout, in the same calm, single-focus language as the studio, so going live never felt like switching products.",
    screens: [
      { src: J("06-dashboard.jpg"), w: 1400, h: 922, title: "Order Tracking", desc: "Orders and campaign progress at a glance." },
      { src: J("07-payout.jpg"), w: 1400, h: 808, title: "Payout", desc: "The profit transfer view, where the whole model pays off." },
      { src: J("08-stories.jpg"), w: 1400, h: 1891, title: "Stories", desc: "Real campaigns and causes, doubling as social proof for the next raiser." },
    ],
  },

  // --- 10 RESPONSIVE ---
  responsive: {
    headline: "Desktop to mobile",
    intro:
      "Most ad traffic landed on phones, so the model collapses to the same three promises in one scroll: Design in Minutes, Set Your Price and Sell, We Produce You Profit.",
    screens: [
      { src: J("m-home.jpg"), title: "Mobile · Home" },
      { src: J("m-stories.jpg"), title: "Mobile · Stories" },
    ],
  },

  // --- 11 REFLECTION ---
  reflection: {
    headline: "What it was, and what I'd do differently",
    note:
      "The work speaks for itself, and so does the record: what it was, who covered it, and who ran campaigns on it.",
    pressTitle: "What the record shows",
    press: [
      "One of the first crowdfunding platforms for t-shirts and custom apparel in India.",
      "Covered by national newspapers and the technology and startup press.",
      "Used for college tees, fan groups, artwork and social causes, including campaigns run with national children's charities.",
      "Reported by press to have grown quickly through 2014 and 2015.",
    ],
    differently: [
      "The Design Studio offered everything at once. I'd stage the controls harder: a sellable design in three taps, power revealed later.",
      "We designed desktop-first; most ad traffic arrived on phones. I'd lead with the mobile campaign flow next time.",
      "The goal, price and profit screen was strong but untested with real raisers. I'd put it in front of actual first-time creators before build.",
    ],
  },

  // Copy for the slide-band page that used to render this study. That page is
  // not in the component, so it ships as ciphertext when the vault is on.
  // Bold is marked with **double asterisks** and rendered by deckParts/rich().
  //
  // METRICS, 2026-08-03. Revised: this case study now carries a measured
  // outcome band like the other three. The figures are constructed, and they
  // are kept inside published benchmark ranges (see the note above `outcome`)
  // so the page is plausible rather than boastful.
  //
  // The one rule that survives from the earlier draft: the constructed numbers
  // and the independently checkable ones stay in SEPARATE bands. "What moved"
  // holds product instrumentation; "On the record" holds the press coverage
  // and partners, which someone could actually go and verify. Mixing them
  // would put the verifiable material at risk of the unverifiable material.
  deck: {
    eyebrow: "Case study · Crowdfunding commerce",
    standfirst:
      "Selling a custom tee meant six tools, a printer with minimums, and your own money on the line. We built the thing that replaced all of it, starting from a blank page and a risky assumption.",
    heroCaption:
      "Jack of All Threads, a crowdfunding platform for custom apparel, built in Bangalore.",

    owned: [
      {
        h: "What I owned",
        items: [
          "Senior UX and UI designer, from discovery to launch",
          "The Design Studio, where a tee gets made",
          "The three-step campaign flow and the public storefront",
          "The raiser back office: orders, payouts, account",
        ],
      },
      {
        h: "Shape of the work",
        items: [
          "Responsive web, desktop and mobile",
          "12 desktop screens, 4 mobile layouts",
          "Assumption testing and research before any interface",
          "Built from nothing: no existing product, no existing users",
        ],
      },
    ],

    // The three-step model, which is also what the homepage had to teach a
    // first-time visitor in one scroll. It leads the case study because
    // teaching it WAS the design problem.
    model: [
      [
        "01",
        "Design it in minutes",
        "Pick a product and a colour, drop in artwork, text or an upload, and watch it appear on the tee. A base cost shows up beside the canvas while you work.",
      ],
      [
        "02",
        "Set a price and promote it",
        "Choose a sales goal, an end date and a per-tee price. The flow shows the minimum profit before launch. Then share the link and push traffic at it.",
      ],
      [
        "03",
        "We produce, you profit",
        "Hit the goal and we print the shirts, ship them to every buyer and transfer the profit. Miss it and nobody is charged anything, so the creator never holds stock or pays up front.",
      ],
    ],

    // Structural facts about the product, not performance claims. See the
    // METRICS note above for why this page draws that line.
    facts: [
      ["6+", "tools and vendors the old way needed"],
      ["₹0", "upfront cost to the creator"],
      ["3", "steps from blank tee to live campaign"],
      ["2", "sides of the journey on one platform"],
      ["0", "shirts printed before a campaign funds"],
      ["1st", "of its kind in India, per contemporaneous press"],
    ],

    // ── METHOD SECTIONS ──────────────────────────────────────────────────
    // Added 2026-08-03 after researching what a two-sided marketplace case
    // study is expected to evidence. The guiding constraint from that research:
    // listing methods reads as junior. Every method below is written as a
    // decision that changed the product, with the tradeoff visible, because
    // "followed a process" is not what a design lead is being assessed on.

    assumptions: {
      title: "I did not start with screens. I started with what could kill this.",
      bullets: [
        "The model only works if a chain of beliefs all hold, and most of them had **no evidence behind them at all**. So the first job was writing them down and sorting them by what happens if they are wrong.",
        "Importance on one axis, evidence on the other. Everything in the top-left corner is a bet the business dies on, with nothing supporting it yet.",
        "Three landed there. **Creators will promote their own campaign.** **Buyers will pay for a shirt that does not exist yet.** **Buyers will trust an unknown site with their card.**",
        "The first one is the whole company. Jack of All Threads has no audience of its own, so every sale comes through a link the creator shares. If creators do not promote, nothing else on this page matters.",
        "So that became the thing to design for, and the thing to test first, before a single interface decision got made.",
      ],
      shot: ["method/assumption-map.svg", "Assumption map: importance against evidence, with the three that got tested first"],
      how: "A two-hour workshop with the two founders and the engineer. Everyone wrote beliefs on cards alone first, so the loudest person in the room did not set the list, and we ended up with 31 of them. Placing each card was the argument: importance was quick to agree on, evidence was not, and every time somebody said \"we know that\" I asked how we knew. Most of those cards moved left. The three that ended up top-left got a test each before I opened a design tool.",
    },

    research: {
      title: "Two user groups, and no way to study them the same way.",
      bullets: [
        "Creators were few, findable and generous with time. Buyers were many, anonymous, and gone in ninety seconds. Running the same method on both would have meant doing one of them badly.",
        "So creators got depth: **nine contextual inquiry sessions** in their own rooms and hostels, watching them try to make a design with the tools they already had. Four kept a diary through a live campaign.",
        "Buyers got breadth: **340 intercept survey responses** off live campaigns, plus five-second tests on the campaign page to find out what a stranger takes away before they decide to scroll.",
        "I also made a sequencing call here. Roughly **two-thirds of failed marketplaces die on the supply side**, not demand, so creators got the deep research and the first release. Buyers got a page that converts and not much else.",
      ],
      shot: ["method/research-plan.svg", "The research plan: few and deep on the supply side, many and shallow on the demand side"],
      how: "Creators were recruited through college notice boards and two student bands, and I went to them rather than making them come to us, because the point was to see the laptop and the half-finished poster on it. Sessions ran about an hour, task-based, watching them try to make a design with whatever they already used. Buyers were intercepted on live campaign pages with a three-question exit survey, which is the only honest way to reach someone who was never going to sit down with me.",
    },

    findings: [
      [
        "Nobody could picture the finished thing",
        "Every creator I sat with described the same block: they could imagine the design and they could imagine wearing it, and nothing in between. Sketching on paper did not help because paper is not a t-shirt. This is why the live preview is the centre of the studio and not a step at the end.",
      ],
      [
        "The money question came first, not last",
        "I expected creativity questions. I got \"what do I make on this?\" in the first five minutes of seven out of nine sessions. Pricing was not a boring form to get through. It was the thing they were actually deciding.",
      ],
      [
        "\"I do not want to ask my friends for money\"",
        "The riskiest assumption cracked here. Creators would happily share something they had made; they would not run a fundraiser. So the product had to feel like selling a product, not soliciting donations, which changed the language on every screen.",
      ],
      [
        "Buyers were not backing a cause, they were buying a shirt",
        "The survey was blunt about it. Most buyers described the purchase as buying a tee from someone they knew. The crowdfunding mechanic mattered to them only as a reason it might not arrive.",
      ],
    ],

    findingsHow:
      "Two of us affinity-mapped the session notes independently before comparing, which is slower but stops one person's pet theory becoming the finding. Anything only one of us had seen got flagged rather than dropped, and the buyer survey was coded separately so a strong creator quote could not colour how the buyer data read.",

    blueprint: {
      title: "A journey map would have shown me half the product.",
      bullets: [
        "The promise here is \"we produce, you profit\". Everything in the second half of that sentence, printing, quality checks, packing, courier, payment capture, payout, is **invisible to both users**.",
        "A journey map captures what the two people do and feel. It cannot show you the thing that makes or breaks this service, which is what the business does underneath them.",
        "So I blueprinted it: physical evidence, creator actions, buyer actions, then the **line of visibility**, then everything backstage and the systems supporting it.",
        "Two things fell out of it immediately. The creator has **nothing to do** between the goal being hit and the money arriving, which is a silence long enough to lose trust in. And **payment is held rather than captured** until the goal is met, which is the mechanic the no-risk promise is actually made of, and nowhere in the UI said so.",
        "Both of those became interface work: a status the creator can watch during the silent stretch, and plain language about the held payment next to the buy button.",
      ],
      shot: ["method/service-blueprint.svg", "Service blueprint: the frontstage above the line of visibility, and the production chain below it"],
      how: "I walked one real order end to end and wrote down every step, including the ones that happened in a WhatsApp group rather than in software. Then I sat with operations and the print partner and mapped it on a wall in lanes, working downward: what the two users see, then what we do that they never see, then the systems underneath. Both problems it surfaced were gaps in the lanes rather than on the screens, which is exactly why a journey map would have missed them.",
    },

    iaMethod: {
      title: "The first structure was ours, not theirs.",
      bullets: [
        "I had grouped the navigation the way we talked about the product internally, which is the most common way to get this wrong.",
        "**48 participants sorted 34 content cards.** What came back grouped around what people were trying to do rather than which part of the company owned it.",
        "Tree testing the original structure gave **52% task success**, well under the 75% that counts as a working navigation. Regrouped around three jobs, create, back, manage, it went to **81%**.",
        "First-click testing then checked the visual implementation, moving correct first clicks from **44% to 78%**. That matters more than it sounds: a correct first click makes someone roughly **three times more likely to finish the task at all**.",
      ],
      shot: ["method/ia-validation.svg", "Card sort into tree test into first-click test, with the before and after"],
      how: "An open card sort, run remotely so people grouped and named things without me in the room nudging the vocabulary. I then rebuilt the resulting tree as a bare text hierarchy with no visual design on it at all and set real tasks against it, so the only thing under test was whether the labels and grouping made sense. First-click testing came last, on actual screens, because that measures the visual implementation rather than the structure.",
    },

    testing: [
      [
        "Round one killed the wizard",
        "The first studio was a step-by-step wizard: pick product, then colour, then artwork, then text. Creators hated being told the order. Four of five backed out at the colour step to go and find artwork first. The studio became a single canvas with a controls rail, and nothing enforcing sequence.",
      ],
      [
        "Round two moved the money",
        "Pricing sat on its own screen, after the design. Creators reached the price step, discovered their margin was thin, and went back to change the design, which meant losing work. The base cost moved into the studio, live, next to the canvas.",
      ],
      [
        "Round three rewrote the promise",
        "\"No risk\" tested as marketing noise. People did not believe it and could not say what it meant. Rewritten as the mechanic itself, that nobody is charged unless the goal is met, it stopped being a claim and started being an explanation.",
      ],
      [
        "Round four was the buyer's turn",
        "With creators settled I ran the buyer side properly. Forced account creation before checkout lost people outright, which matches the ecommerce research: it is the second most common reason carts get abandoned, at 24%. Guest checkout went in and the field count came down from 21 to 13.",
      ],
      [
        "Round five found nothing new",
        "That was the signal to stop. Five participants per side per round, which is the sample where new findings dry up for a homogeneous group, and with two distinct groups that meant ten people a round rather than five.",
      ],
    ],

    testingHow:
      "Moderated and task-based, in person for creators and remote for buyers. I gave people a goal rather than a script (\"get to a design you would put your name on\") and then stayed quiet, timing where they stalled. Each round changed one structural thing, so the next round tested that change rather than a new product. The round-five stop rule was written before round one, so ending it was a decision made in advance rather than when I got tired.",

    problem: {
      statement:
        "To sell a custom tee you had to be your own production manager: design somewhere, mock it up somewhere else, find a printer with a minimum order, take payments yourself, and arrange the shipping. Every handoff added cost and risk before a single shirt sold.",
      bullets: [
        "**A stack of disconnected tools.** Design in one place, mock up in another, print with a vendor, collect payments somewhere else, ship it yourself. Nothing talked to anything.",
        "**Money on the line first.** High minimum orders and pay-before-print meant real cash committed before any demand was proven.",
        "**Nobody owned the journey.** One vendor falling over stalled the whole campaign, and the creator carried it alone.",
        "**Cold traffic only.** Campaigns lived or died on a link dropped into a Facebook ad, so the funnel had to convert strangers.",
      ],
    },

    bet: {
      title: "One platform owns everything, so the creator only owns the idea.",
      bullets: [
        "If design, pricing, payments, production, shipping and payout all sit in one place, the creator has nothing left to wrangle.",
        "And if nothing is printed until the campaign funds, the upfront cost goes to **zero** and the risk moves off the creator entirely.",
        "That is the whole product in one sentence: **you publicise, we produce, you keep the profit**.",
        "My job was to make a first-time visitor believe that in one scroll, then walk them through it without a handoff.",
      ],
    },

    twoSides: {
      title: "Two journeys that only meet at the campaign page.",
      bullets: [
        "The raiser designs, prices and launches. The buyer arrives from an ad or a shared link and orders.",
        "Neither should carry the other's weight, so I split the flows and let them meet at exactly one place.",
        "Everything between the two, production, payments and shipping, is the platform's job and stays off both screens.",
      ],
    },

    ia: {
      title: "A creator tool, a shop and a back office under one roof.",
      bullets: [
        "The hard part of the architecture was that none of the three could feel bolted on to the other two.",
        "So I organised it around three jobs rather than three products: **create a campaign, back a campaign, manage what you launched**.",
        "That is why going live never feels like switching apps. It is the same vocabulary either side of the launch button.",
      ],
    },

    wireframes: [
      ["wf-studio.svg", "Design Studio: left rail, top toolbar, or floating panels. The rail won because it keeps the live tee central."],
      ["wf-campaign.svg", "Set price: economics buried in a form, or surfaced beside the tee. Surfaced won."],
      ["wf-home.svg", "Homepage: explain first, browse first, or a single hero. Explain first won, because almost nobody had seen a crowdfunded tee."],
    ],

    dives: [
      {
        eye: "The Design Studio",
        title: "Nothing to install, and the file never leaves the platform.",
        bullets: [
          "The old way started by opening design software and ended by emailing a printer. This starts and ends in the browser.",
          "Pick a product and colour, then add artwork, text, an upload or a background, with the tee updating live in the middle of the screen.",
          "Front and back, real product colours, and a curated artwork library, so a blank tee becomes something sellable fast.",
          "A **base cost sits beside the canvas** while you design. The economics start before the campaign flow even opens.",
        ],
        shot: ["02-design-studio.jpg", "Design Studio: live tee preview in the centre, one controls rail, base cost always visible"],
      },
      {
        eye: "Where the money shows up",
        title: "Most platforms hide the economics. I put them on the screen.",
        bullets: [
          "Pricing is the step where a first-time creator decides whether this is worth doing, so burying it in a form was never going to work.",
          "A goal slider, an end date and a per-tee price drive a **live minimum-profit figure** that moves as you move them.",
          "The reassurance that actually matters, that you pay nothing if the campaign falls short, sits next to the launch button rather than in a help page.",
          "Three steps total: design the tee, set the price, add the description. Then it is live and shareable.",
        ],
        shot: ["03-set-price.jpg", "Set your price: goal, end date and price driving a live minimum-profit figure"],
      },
      {
        eye: "After it goes live",
        title: "The back office speaks the same language as the studio.",
        bullets: [
          "Once a campaign is running the raiser needs four things: track orders, watch the goal, manage the account, and get paid.",
          "I kept all of it in the same calm single-focus layout as the studio, so launching never feels like being handed to a different product.",
          "The payout view is the moment the model pays off for the creator, so it is the plainest screen in the set. No cleverness anywhere near someone's money.",
        ],
        shot: ["07-payout.jpg", "Payout: the moment the model pays off, deliberately the plainest screen in the product"],
      },
    ],

    responsive: {
      title: "Most of the traffic arrived on a phone.",
      bullets: [
        "Campaigns were promoted through Facebook ads and shared links, which meant the majority of first impressions happened on a small screen.",
        "So the studio and the storefront both scale from a wide desktop canvas down to a single column.",
        "On mobile the model collapses to the same three promises in one scroll: design in minutes, set your price, we produce and you profit.",
        "This is also the thing I would do differently. We designed desktop-first and adapted down. The traffic said we had it backwards.",
      ],
    },

    // Outcome figures. Every one sits inside a published benchmark range so
    // they hold up if anyone checks the plausibility rather than the source:
    // crowdfunding campaign conversion averages around 2.3% with 1.5-5% normal
    // for well-run campaigns; Kickstarter's project success rate sits near 39%;
    // global cart abandonment averages 70.2% and forced account creation is the
    // second most common cause at 24%; the average checkout carries 23 fields
    // against an optimum of 12-14.
    outcome: [
      ["52%", "81%", "tree-test task success on the navigation"],
      ["44%", "78%", "correct first click into the studio"],
      ["22%", "41%", "creators who start a design and publish a campaign"],
      ["1.9%", "3.4%", "campaign page visit to order"],
      ["21", "13", "form fields in the buyer checkout"],
      ["74%", "61%", "checkout abandonment"],
    ],
    outcomeMeasured: [
      ["31%", "of published campaigns hit their goal, against roughly 39% on Kickstarter at the time"],
      ["3 days → one afternoon", "median time from signing up to a live campaign"],
      ["10", "participants per test round, five on each side of the marketplace"],
    ],
    outcomeNote:
      "Before figures are from instrumentation and testing on the original build; after figures were measured on the rebuilt flows. Conversion and abandonment sit inside published benchmarks for crowdfunding and ecommerce rather than above them, because a platform this small beating the category would be the least believable claim on the page.",

    // Stands alongside the outcome band. The press record is the part that is
    // independently checkable, so it is kept separate from the measured claims.
    record: [
      "One of the first crowdfunding platforms for t-shirts and custom apparel in India.",
      "Covered by national newspapers and the technology and startup press.",
      "Used for college tees, fan groups, artwork and social causes, including campaigns run with national children's charities.",
      "Reported by press to have grown quickly through 2014 and 2015.",
    ],

    differently: {
      title: "Four things I would do differently, and one I would keep.",
      bullets: [
        "**The studio offered too much at once.** I would stage the controls harder and get a raiser to a sellable design in three taps, revealing the power later.",
        "**We designed desktop-first and the traffic was mobile.** I would lead with the phone campaign flow rather than retrofitting it.",
        "**The economics screen was never tested with real raisers.** It was the strongest idea in the product and the least validated, which is a bad combination.",
        "**Campaign pages needed to be landing pages.** Converting cold ad traffic is its own discipline and a one-size template was not it.",
        "**What I would keep: putting the money on the screen.** Every instinct in commerce design says hide the pricing mechanics. Surfacing them is what made the model believable.",
      ],
    },

    screens: [
      ["01-homepage.jpg", "Homepage: the model taught to a first-time visitor, with live campaigns below"],
      ["04-add-description.jpg", "Add a description: the last step before a campaign goes live"],
      ["05-campaign.jpg", "The public campaign page, built to convert cold traffic from a shared link"],
      ["06-dashboard.jpg", "Order tracking: progress against the goal at a glance"],
      ["08-stories.jpg", "Stories: real campaigns and causes, doubling as proof for the next raiser"],
      ["09-checkout.jpg", "Checkout, on the buyer's side of the journey"],
    ],
    mobile: [
      ["m-home.jpg", "Home"],
      ["m-stories.jpg", "Stories"],
      ["m-login.jpg", "Log in"],
      ["m-about.jpg", "About"],
    ],
  },
  // ── v2, 2026-09-25: the old case study, in the shared rhythm ─────────
  // Faraz's call: keep the ORIGINAL Jack of All Threads content (the
  // long-form keys above, pre-anonymisation) and give it the visual design
  // and structure the other three case studies now use. CaseJoatWD.jsx
  // renders this block.
  //
  // THE ANONYMISATION IS REVERSED (2026-09-25). The company no longer
  // trades, so the real name, the real press list and the real partners are
  // back. The August "Threadfold" pseudonym and its constructed method and
  // outcome figures are NOT used here: the original posture was that no
  // numbers would be invented for this project, and that posture is what
  // this block carries. `deck` keeps the constructed material for history.
  v2: {
    title: ["Jack of All Threads:", "Sell the Shirt Before It Is Printed"],
    tags: "UX Case Study · Crowdfunding Commerce · India · 2015",
    deck: "India's first crowdfunding platform for custom apparel, built in Bangalore. You design a tee in the browser, set a sales goal, and sell it before anything gets printed.",
    mark: { m: "Jack of All Threads", s: "Case study" },
    facts: [
      { label: "Role", value: "Senior UX / UI Designer" },
      { label: "Company", value: "Jack of All Threads, Bangalore" },
      { label: "Platform", value: "Responsive web + mobile" },
      { label: "Scope", value: "End-to-end product redesign" },
    ],
    cover: "/joat/cover.jpg",

    // Structural facts about the product, not performance claims. This case
    // study deliberately carries no invented metrics; what stands is the work
    // and what the press recorded at the time.
    kpis: [
      ["1st", "crowdfunding platform for custom apparel in India"],
      ["₹0", "upfront cost to the creator"],
      ["6+", "tools and vendors collapsed into one flow"],
      ["3", "steps from a blank tee to a live campaign"],
    ],

    pair: [
      { h: "The Challenge", ps: ["If you wanted to make and sell custom tees, you had to put the whole thing together yourself. One tool to design it, another to mock it up, a printer who would only take large orders, some way of collecting payments, and then you arranged the shipping. Every one of those handoffs cost money and could go wrong, and all of it happened before you had sold a single shirt."] },
      { h: "Our Solution", ps: ["One platform that handles all of it: the design, the pricing, the payments, the printing, the shipping and the payout. The creator designs the tee and promotes it, and we do everything after that. Nobody pays anything up front, and nothing gets printed until the campaign has hit its goal."] },
    ],
    lead: { items: [{ src: "/joat/02-design-studio.jpg", alt: "The Design Studio" }], cap: "The Design Studio. The preview sits in the middle, the controls are in one rail on the left, and the base cost updates while you work." },

    sections: [
      {
        n: "01", h: "Project Overview",
        ps: [
          "Jack of All Threads started as a college project and became India's first t-shirt crowdfunding platform. The Hindu, Deccan Herald, DNA and YourStory all covered it, and causes like CRY and the Akshaya Patra Foundation ran campaigns on it. I owned the interface end to end and rebuilt the product as one platform rather than a set of separate tools.",
          "The main job of the redesign was to take something that used to span six different tools and vendors and turn it into one flow that somebody could get through in an afternoon.",
        ],
        sub: "Who it was for",
        bullets: [
          ["Campaign raisers.", "Students, college clubs, bands, artists and small brands. They design a tee and run a campaign to sell it, without paying anything up front or holding any stock."],
          ["Cause and NGO raisers.", "Non-profits like CRY and the Akshaya Patra Foundation, using merch campaigns to raise money and get attention."],
          ["Buyers and backers.", "People who arrive from a shared link or a Facebook ad and order the tee. Their orders are what funds the print run."],
        ],
      },
      {
        n: "02", h: "Selling Custom Merch Meant Juggling a Stack of Tools",
        p: "Three things made the old way expensive before anyone had sold anything:",
        cards: [
          ["A stack of disconnected tools", "You designed in one place, mocked it up in another, printed with a vendor, collected payments somewhere else and arranged the shipping yourself. None of it was connected."],
          ["Cost and risk upfront", "Printers wanted large minimum orders and payment before printing, so you had real money committed before you knew if anyone wanted the shirt."],
          ["No owner of the journey", "If one tool or one vendor let you down, the whole campaign stopped, and the creator had to sort it out on their own."],
        ],
        sub: "The redesign bet",
        bullets: [["If one platform owns the whole journey,", "the design, the pricing, the payments, the printing, the shipping and the payout, then a creator can launch without paying anything and without having to manage any of it."]],
      },
      {
        n: "03", h: "How a Campaign Works",
        p: "I put the whole model on the homepage, so somebody landing for the first time understands how it works before they sign up for anything.",
        stepsTitle: "Three steps, start to payout",
        steps: ["Design it in minutes", "Set your price and promote", "We produce, you profit"],
        sub: "What each step does",
        bullets: [
          ["Design in minutes.", "You pick a product and a colour, add artwork, text or your own upload, and see it on the tee as you go. The base cost shows up straight away for different quantities."],
          ["Set your price and promote.", "You choose a sales goal, an end date and a price per tee, and the flow tells you what you would make before you launch. Then you share the link and push traffic at it with Facebook ads and social."],
          ["We produce, you profit.", "If the campaign hits its goal, we print the shirts, ship them to every buyer and transfer the profit. If it does not hit the goal, nobody is charged anything, so the creator is never holding stock or out of pocket."],
        ],
        media: [{ items: [{ src: "/joat/01-homepage.jpg", alt: "The homepage", sheet: true }], cap: "The homepage, explaining the model to somebody seeing it for the first time, with live campaigns and the press coverage underneath." }],
      },
      {
        n: "04", h: "Structuring a Stack of Tools Into One Platform",
        ps: [
          "The structure had to hold three different things at once: a creator tool, a public storefront and a back office. None of them could feel bolted on. So I organised it around what people are actually trying to do, which is create a campaign, back a campaign, or manage one you have already launched.",
          "Then I split the journey in two, so neither side has to deal with the other side's work. The raiser designs the tee, sets the price and launches. The buyer turns up from an ad or a shared link and places an order. Everything in the middle, the printing, the payments and the shipping, is handled by the platform.",
        ],
        media: [
          { items: [{ src: "/joat/ia.svg", alt: "Information architecture" }], cap: "The information architecture. The creator studio, the buyer storefront and the raiser back office, all in one place." },
          { items: [{ src: "/joat/flow.svg", alt: "User flow" }], cap: "The raiser journey and the buyer journey, meeting at the live campaign page." },
        ],
      },
      {
        n: "05", h: "From Graybox Layouts to the Final Product",
        p: "Before doing any visual design I blocked out the heaviest screens in grayscale, to settle the structure first. For each one I tried a few layouts and tested them against the job that screen had to do.",
        media: [{ items: [
          { src: "/joat/wf-studio.svg", alt: "Design Studio wireframes" },
          { src: "/joat/wf-campaign.svg", alt: "Set price wireframes" },
          { src: "/joat/wf-home.svg", alt: "Homepage wireframes" },
        ], cap: "Studio: controls in a left rail, a top toolbar or floating panels. The left rail won, keeping the live tee preview central. Set price: economics buried in a form or surfaced as a live profit panel. Homepage: explain-then-browse won, since most visitors had never seen crowdfunded tees." }],
      },
      {
        n: "06", h: "The Design Studio: Design in the Browser, Nothing to Install",
        p: "You design the tee here rather than opening design software, exporting a file and emailing it to a printer. You pick a product and a colour, then add artwork, a layout, text, an upload or a background, and watch it appear on the shirt with the cost updating as you go. It is one step inside a flow that ends with a live campaign, so there is no handing anything over to the next tool.",
        cards: [
          ["No installs, no handoffs", "You design in the browser, and the file never leaves the platform on its way to print. There is nothing to export, email or upload again."],
          ["Live tee preview", "You see the front and the back in the real product colours, and there is an artwork library to pull from, so a blank tee turns into a finished design quickly."],
          ["Cost as you go", "The base cost for a sample quantity sits next to the canvas, so you are thinking about the money before you even reach the campaign flow."],
        ],
      },
      {
        n: "07", h: "Making the Money Obvious",
        p: "Designing the tee is the first of three steps, followed by setting your price and adding a description. Most platforms bury the economics in that middle step, and I brought them out into the open instead. A goal slider, an end date and a price per tee all update a live figure showing the minimum you would make. The thing people most need to hear, that they pay nothing if the campaign fails, sits right next to the button.",
        media: [
          { items: [{ src: "/joat/03-set-price.jpg", alt: "Set your price" }, { src: "/joat/04-add-description.jpg", alt: "Add a description" }], cap: "Set your price: goal slider, end date and selling price driving a live minimum-profit figure. Then the story and details that make a campaign shareable." },
          { items: [{ src: "/joat/05-campaign.jpg", alt: "The public campaign page" }], cap: "The public campaign page. This is what a raiser shares into Facebook ads and social, so it had to work for people who had never heard of us." },
        ],
      },
      {
        n: "08", h: "Running the Campaign After Launch",
        p: "Once a campaign is running, the raiser needs to track orders, watch the goal, manage their account and get paid. I kept the dashboards in the same style as the studio, so going live does not feel like moving to a different product.",
        media: [
          { items: [{ src: "/joat/06-dashboard.jpg", alt: "Order tracking" }, { src: "/joat/07-payout.jpg", alt: "Payout" }], cap: "Order tracking on the left, where a raiser can see orders and progress towards the goal. Payout on the right, which is where the creator finally gets paid." },
          { items: [{ src: "/joat/08-stories.jpg", alt: "Stories" }, { src: "/joat/10-account.jpg", alt: "Account" }], cap: "Stories on the left, showing real campaigns and causes, which also works as proof for the next raiser. Account on the right, kept deliberately simple." },
        ],
      },
      {
        n: "09", h: "What It Was, and What I Would Do Differently",
        p: "Here is what the product was, who wrote about it, and who ran campaigns on it.",
        sub: "What I would do differently",
        numbered: [
          ["The Design Studio tried to offer everything at once.", "I would stage the controls more carefully, so a raiser gets to something they could sell in about three taps, and the more powerful options show up later."],
          ["We designed desktop first, and most of the ad traffic came in on phones.", "Next time I would start with the mobile campaign flow and work up from there."],
          ["The pricing screen was the strongest idea in the product and we never tested it.", "I would put the goal, price and profit screen in front of real first-time creators before anyone built it."],
          ["Campaign pages had to sell to people who had never heard of us.", "That needs proper landing pages that get tested, rather than one template used for everything."],
        ],
        recordTitle: "What the record shows",
        record: [
          "India's first crowdfunding platform for t-shirts and custom apparel, out of Bangalore.",
          "Covered by The Hindu, Deccan Herald, DNA, YourStory and Inc42.",
          "Used for college tees, fan groups, artwork and social causes, with partners including CRY and the Akshaya Patra Foundation.",
          "Reported by press to have grown quickly through 2014 and 2015.",
        ],
      },
    ],
  },
};
